import { Select } from '@mantine/core';
import { useGetDebtorsInfinite } from 'api';
import { i18nKeys, useTranslation } from 'locales';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router';
import { flat, map, pipe } from 'remeda';
import { treatClient } from 'shared/serializer';
import { getSearchInvoicesUrlParams } from 'shared/utils/hooks';
import { getNextPageParam } from 'shared/utils/query';
import { clientActions } from 'store/client/client.actions';
import type { Debtor } from 'types/store/client-state';

import { useInvoiceFormContext } from '../InvoiceForm.context';

type DebtorFieldProps = {
  onSelectDebtor: (debtor: Debtor) => void;
};

export const DebtorField = ({ onSelectDebtor }: DebtorFieldProps) => {
  const { t } = useTranslation();
  const form = useInvoiceFormContext();
  const scrollRef = useRef<HTMLDivElement>(null);

  const currentDebtor = form.values.debtor_attributes;

  const [searchValue, setSearchValue] = useState<string>(currentDebtor?.full_name ?? '');
  const {
    data: debtorsResponse,
    fetchNextPage,
    hasNextPage,
  } = useGetDebtorsInfinite(
    { name: searchValue || undefined },
    {
      query: {
        getNextPageParam,
      },
    },
  );

  const location = useLocation();
  const params = getSearchInvoicesUrlParams(location);

  useEffect(() => {
    setSearchValue(currentDebtor?.full_name ?? '');
  }, [currentDebtor?.full_name]);

  useLayoutEffect(() => {
    const { debtor_id } = params;

    if (!currentDebtor && debtor_id) {
      clientActions.detail({
        id: Number(debtor_id),
        callback: ({ data: dataDebtor }: { data: unknown }) => {
          const foundDebtor = treatClient(dataDebtor);

          if (!foundDebtor) return;

          onSelectDebtor(foundDebtor);
          setSearchValue(foundDebtor.full_name);
        },
      });
    }
  }, []);

  const pages = debtorsResponse?.pages ?? [];

  const debtors = useMemo(
    () =>
      pipe(
        pages,
        map(e => e.data),
        flat(),
        map(treatClient),
      ),
    [pages],
  );

  const selectData = useMemo(
    () =>
      debtors.map(debtor => ({
        value: String(debtor.id),
        label: debtor.full_name,
      })),
    [debtors],
  );

  const handleChange = useCallback(
    (selectedId: string | null) => {
      if (!selectedId) return;
      const debtor = debtors.find(d => String(d.id) === selectedId);
      if (debtor) {
        onSelectDebtor(debtor);
        setSearchValue(debtor.full_name);
      }
    },
    [debtors, onSelectDebtor],
  );

  const handleScrollPositionChange = useCallback(
    ({ y }: { x: number; y: number }) => {
      if (!scrollRef.current || !hasNextPage) return;

      const { scrollHeight, clientHeight } = scrollRef.current;
      if (y + clientHeight >= scrollHeight - 50) {
        fetchNextPage();
      }
    },
    [fetchNextPage, hasNextPage],
  );

  return (
    <Select
      data={selectData}
      value={currentDebtor ? String(currentDebtor.id) : null}
      onChange={handleChange}
      searchable
      searchValue={searchValue}
      onSearchChange={setSearchValue}
      filter={({ options }) => options}
      placeholder={t(i18nKeys.INVOICING.CREATE.SELECT_CLIENT)}
      size="xs"
      scrollAreaProps={{
        onScrollPositionChange: handleScrollPositionChange,
        viewportRef: scrollRef,
      }}
    />
  );
};
